import React, { useState, useEffect } from 'react';
import { Users, Plus, Pencil, Trash2, X, User, Mail, Phone, AlertCircle, FileText, Upload, Image, Search, Wallet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface TeamMember {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: 'hostess' | 'promoter' | 'influencer' | 'cassiere' | 'band' | 'fotografo';
  organizer_id: string | null;
  notes: string | null;
  photo_url: string | null;
  has_revolut: boolean;
  created_at: string;
  created_by: string | null;
  organizer?: {
    id: string;
    name: string;
  };
}

interface Profile {
  id: string;
  name: string;
}

const roleLabels: Record<string, string> = {
  hostess: 'Hostess',
  promoter: 'Promoter',
  influencer: 'Influencer',
  cassiere: 'Cassiere',
  band: 'Band',
  fotografo: 'Fotografo'
};

const Team = () => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [organizers, setOrganizers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [activeRole, setActiveRole] = useState<'all' | string>('all');
  const [viewingMember, setViewingMember] = useState<TeamMember | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'hostess' as 'hostess' | 'promoter' | 'influencer' | 'cassiere' | 'band' | 'fotografo',
    organizer_id: '',
    notes: '',
    has_revolut: false
  });

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    loadTeamMembers();
    if (user?.role === 'admin') {
      loadOrganizers();
    }
  }, [user]);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('team_members')
        .select(`
          *,
          organizer:profiles!team_members_organizer_id_fkey (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (user?.role === 'organizer') {
        query = query.eq('organizer_id', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;
      setTeamMembers(data || []);
    } catch (error: any) {
      console.error('Error loading team members:', error);
      setError(error.message || 'Errore nel caricamento dei membri del team');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, name')
        .eq('role', 'organizer')
        .order('name');

      if (error) throw error;
      setOrganizers(data || []);
    } catch (error: any) {
      console.error('Error loading organizers:', error);
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('La foto deve essere inferiore a 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Il file deve essere un\'immagine');
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async (): Promise<string | null> => {
    if (!photoFile) return null;

    try {
      setUploadingPhoto(true);
      const fileExt = photoFile.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('team-member-photos')
        .upload(filePath, photoFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('team-member-photos')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      throw error;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      let photoUrl = editingMember?.photo_url || null;

      if (photoFile) {
        photoUrl = await uploadPhoto();
      }

      const memberData = {
        name: formData.name.toUpperCase(),
        email: formData.email || null,
        phone: formData.phone || null,
        role: formData.role,
        organizer_id: user?.role === 'admin' && formData.organizer_id
          ? formData.organizer_id
          : user?.role === 'organizer'
            ? user.id
            : null,
        notes: formData.notes || null,
        photo_url: photoUrl,
        has_revolut: formData.has_revolut
      };

      if (editingMember) {
        const { error } = await supabase
          .from('team_members')
          .update(memberData)
          .eq('id', editingMember.id);

        if (error) throw error;
      } else {
        const { data: { user: currentUser } } = await supabase.auth.getUser();

        const { error } = await supabase
          .from('team_members')
          .insert([{ ...memberData, created_by: currentUser?.id }]);

        if (error) throw error;
      }

      await loadTeamMembers();
      closeModal();
    } catch (error: any) {
      console.error('Error saving team member:', error);
      setError(error.message || 'Errore nel salvare il membro del team');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      email: member.email || '',
      phone: member.phone || '',
      role: member.role,
      organizer_id: member.organizer_id || '',
      notes: member.notes || '',
      has_revolut: member.has_revolut || false
    });
    setPhotoPreview(member.photo_url);
    setPhotoFile(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (memberId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo membro del team?')) {
      try {
        const { error } = await supabase
          .from('team_members')
          .delete()
          .eq('id', memberId);

        if (error) throw error;
        await loadTeamMembers();
      } catch (error: any) {
        console.error('Error deleting team member:', error);
        setError(error.message || 'Errore nell\'eliminazione del membro del team');
      }
    }
  };

  const handleRevolutToggle = async (memberId: string, currentValue: boolean) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .update({ has_revolut: !currentValue })
        .eq('id', memberId);

      if (error) throw error;
      await loadTeamMembers();
    } catch (error: any) {
      console.error('Error updating revolut status:', error);
      setError(error.message || 'Errore nell\'aggiornamento dello stato Revolut');
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingMember(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'hostess',
      organizer_id: '',
      notes: '',
      has_revolut: false
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setError(null);
  };

  const filteredMembers = teamMembers.filter(member => {
    const matchesRole = activeRole === 'all' || member.role === activeRole;
    const matchesSearch = searchQuery === '' ||
      member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.phone?.includes(searchQuery);
    return matchesRole && matchesSearch;
  });

  const getRoleCounts = () => {
    return {
      hostess: teamMembers.filter(m => m.role === 'hostess').length,
      promoter: teamMembers.filter(m => m.role === 'promoter').length,
      influencer: teamMembers.filter(m => m.role === 'influencer').length,
      cassiere: teamMembers.filter(m => m.role === 'cassiere').length,
      band: teamMembers.filter(m => m.role === 'band').length,
      fotografo: teamMembers.filter(m => m.role === 'fotografo').length
    };
  };

  const roleCounts = getRoleCounts();

  if (loading && teamMembers.length === 0) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Team</h1>
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  const totalMembers = teamMembers.length;
  const membersWithRevolut = teamMembers.filter(m => m.has_revolut).length;

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Totale Membri</p>
                  <p className="text-3xl font-bold text-white mt-1">{totalMembers}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Hostess</p>
                  <p className="text-3xl font-bold text-white mt-1">{roleCounts.hostess}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <User className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Promoter</p>
                  <p className="text-3xl font-bold text-white mt-1">{roleCounts.promoter}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <User className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Revolut</p>
                  <p className="text-3xl font-bold text-white mt-1">{membersWithRevolut}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Wallet className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Cerca collaboratori..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-rose-500 focus:border-rose-500"
          />
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-gradient-to-br from-rose-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all flex items-center gap-2 text-sm font-medium shadow-lg"
        >
          <Plus className="h-4 w-4" />
          Nuovo Collaboratore
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4 px-4 overflow-x-auto" aria-label="Tabs">
            <button
              onClick={() => setActiveRole('all')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeRole === 'all'
                  ? 'border-rose-500 text-rose-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Tutti ({teamMembers.length})
            </button>
            <button
              onClick={() => setActiveRole('hostess')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeRole === 'hostess'
                  ? 'border-rose-500 text-rose-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Hostess ({roleCounts.hostess})
            </button>
            <button
              onClick={() => setActiveRole('promoter')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeRole === 'promoter'
                  ? 'border-rose-500 text-rose-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Promoter ({roleCounts.promoter})
            </button>
            <button
              onClick={() => setActiveRole('influencer')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeRole === 'influencer'
                  ? 'border-rose-500 text-rose-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Influencer ({roleCounts.influencer})
            </button>
            <button
              onClick={() => setActiveRole('cassiere')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeRole === 'cassiere'
                  ? 'border-rose-500 text-rose-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Cassieri ({roleCounts.cassiere})
            </button>
            <button
              onClick={() => setActiveRole('band')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeRole === 'band'
                  ? 'border-rose-500 text-rose-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Band ({roleCounts.band})
            </button>
            <button
              onClick={() => setActiveRole('fotografo')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeRole === 'fotografo'
                  ? 'border-rose-500 text-rose-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Fotografi ({roleCounts.fotografo})
            </button>
          </nav>
        </div>

        <div className="p-4">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Foto</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Nome</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Ruolo</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Email</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Telefono</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Revolut</th>
                {user?.role === 'admin' && (
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Organizzatore</th>
                )}
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length > 0 ? (
                filteredMembers.map((member) => (
                  <tr key={member.id} className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      {member.photo_url ? (
                        <img
                          src={member.photo_url}
                          alt={member.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-500" />
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 font-medium">
                      <button
                        onClick={() => setViewingMember(member)}
                        className="text-rose-600 hover:text-rose-700 hover:underline transition-colors"
                      >
                        {member.name}
                      </button>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {roleLabels[member.role]}
                      </span>
                    </td>
                    <td className="py-3 px-4">{member.email || '-'}</td>
                    <td className="py-3 px-4">{member.phone || '-'}</td>
                    <td className="py-3 px-4 text-center">
                      <input
                        type="checkbox"
                        checked={member.has_revolut}
                        onChange={() => handleRevolutToggle(member.id, member.has_revolut)}
                        className="h-4 w-4 text-rose-600 border-gray-300 rounded cursor-pointer focus:ring-rose-500"
                      />
                    </td>
                    {user?.role === 'admin' && (
                      <td className="py-3 px-4">{member.organizer?.name || '-'}</td>
                    )}
                    <td className="py-3 px-4">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => handleEdit(member)}
                          className="text-blue-600 hover:text-blue-700 p-1 hover:bg-blue-50 rounded"
                          title="Modifica"
                        >
                          <Pencil className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="text-rose-600 hover:text-rose-700 p-1 hover:bg-rose-50 rounded"
                          title="Elimina"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={user?.role === 'admin' ? 8 : 7} className="py-8 text-center text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>Nessun collaboratore</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Dettagli Collaboratore</h2>
              <button
                onClick={() => setViewingMember(null)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="flex justify-center">
                {viewingMember.photo_url ? (
                  <img
                    src={viewingMember.photo_url}
                    alt={viewingMember.name}
                    className="h-32 w-32 rounded-full object-cover border-4 border-gray-200 shadow-lg"
                  />
                ) : (
                  <div className="h-32 w-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-200 shadow-lg">
                    <User className="h-16 w-16 text-gray-500" />
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Nome</label>
                  <p className="text-lg font-semibold text-gray-900">{viewingMember.name}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Ruolo</label>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {roleLabels[viewingMember.role]}
                  </span>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </label>
                  <p className="text-gray-900">
                    {viewingMember.email ? (
                      <a href={`mailto:${viewingMember.email}`} className="text-rose-600 hover:text-rose-700 hover:underline">
                        {viewingMember.email}
                      </a>
                    ) : (
                      <span className="text-gray-400">Non specificata</span>
                    )}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Telefono
                  </label>
                  <p className="text-gray-900">
                    {viewingMember.phone ? (
                      <a href={`tel:${viewingMember.phone}`} className="text-rose-600 hover:text-rose-700 hover:underline">
                        {viewingMember.phone}
                      </a>
                    ) : (
                      <span className="text-gray-400">Non specificato</span>
                    )}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Revolut</label>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={viewingMember.has_revolut}
                      readOnly
                      className="h-4 w-4 text-rose-600 border-gray-300 rounded pointer-events-none"
                    />
                    <span className="ml-2 text-gray-900">{viewingMember.has_revolut ? 'Sì' : 'No'}</span>
                  </div>
                </div>

                {user?.role === 'admin' && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Organizzatore
                    </label>
                    <p className="text-gray-900">
                      {viewingMember.organizer?.name || <span className="text-gray-400">Non assegnato</span>}
                    </p>
                  </div>
                )}

                {viewingMember.notes && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Note
                    </label>
                    <p className="text-gray-900 whitespace-pre-wrap">{viewingMember.notes}</p>
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Data di creazione</label>
                  <p className="text-gray-900">
                    {new Date(viewingMember.created_at).toLocaleDateString('it-IT', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setViewingMember(null);
                    handleEdit(viewingMember);
                  }}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Pencil className="h-4 w-4" />
                  Modifica
                </button>
                <button
                  onClick={() => setViewingMember(null)}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {editingMember ? 'Modifica Collaboratore' : 'Nuovo Collaboratore'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ruolo *
                </label>
                <select
                  required
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                >
                  <option value="hostess">Hostess</option>
                  <option value="promoter">Promoter</option>
                  <option value="influencer">Influencer</option>
                  <option value="cassiere">Cassiere</option>
                  <option value="band">Band</option>
                  <option value="fotografo">Fotografo</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Foto
                </label>
                <div className="space-y-2">
                  {photoPreview && (
                    <div className="flex items-center gap-3">
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="h-20 w-20 rounded-full object-cover border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPhotoFile(null);
                          setPhotoPreview(null);
                        }}
                        className="text-sm text-rose-600 hover:text-rose-700"
                      >
                        Rimuovi foto
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <label className="cursor-pointer bg-white px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm">
                      <Upload className="h-4 w-4" />
                      {photoPreview ? 'Cambia foto' : 'Carica foto'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                    </label>
                    <span className="text-xs text-gray-500">Max 5MB</span>
                  </div>
                </div>
              </div>

              {user?.role === 'admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organizzatore
                  </label>
                  <select
                    value={formData.organizer_id}
                    onChange={(e) => setFormData({ ...formData, organizer_id: e.target.value })}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  >
                    <option value="">Seleziona organizzatore</option>
                    {organizers.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefono
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="has_revolut"
                  checked={formData.has_revolut}
                  onChange={(e) => setFormData({ ...formData, has_revolut: e.target.checked })}
                  className="h-4 w-4 text-rose-600 focus:ring-rose-500 border-gray-300 rounded"
                />
                <label htmlFor="has_revolut" className="ml-2 block text-sm text-gray-700">
                  Revolut
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700 disabled:opacity-50"
                >
                  {editingMember ? 'Salva' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;
